import type { RhythmScore } from '@/lib/exercise/rhythmEngine';
import { RHYTHM_ACCURACY_GOAL } from '@/lib/progress/gating';

export type RhythmItemKey = 'note' | 'attack' | 'duration' | 'rest' | 'tie';

export interface RhythmItemSummary {
  key: RhythmItemKey;
  label: string;
  icon: string;
  /** quantas vezes o item foi considerado CERTO */
  correct: number;
  /** quantas vezes o item foi considerado ERRADO */
  wrong: number;
  /**
   * O item faz parte do conteúdo desta aula?
   * Só os itens aplicáveis entram no critério de domínio.
   */
  applicable: boolean;
  /**
   * O item não pôde ser MEDIDO (ex.: duração sem fim de nota informado pelo
   * aparelho). Não conta como certo nem como errado, mas impede declarar
   * "execução sem erros".
   */
  notEvaluated: boolean;
  /** explicação exata do que o motor contou como certo e como errado */
  rule: string;
}

export interface RhythmSummaryContext {
  hasRests: boolean;
  hasTies: boolean;
  /** a aula exige sustentar a nota (modo "no pulso")? Na marcação de pulso, não. */
  measuresDuration: boolean;
}

/**
 * Traduz o placar bruto do motor em um resumo por item, com a regra exata de
 * avaliação de cada um. É a mesma leitura em qualquer aula de ritmo — só os
 * itens que a aula realmente cobra aparecem (`applicable`).
 */
export function buildRhythmSummary(
  score: RhythmScore,
  context: RhythmSummaryContext,
): RhythmItemSummary[] {
  const rightPitch = score.correct + score.early + score.late;
  // "heldToEnd" só vira acerto quando o aparelho comprovadamente informa o fim
  // da nota (houve pelo menos um note-off); sem isso, é "não avaliada".
  const trustHolds = score.noteOffCount > 0;
  const durationCorrect = score.durationsHeld + (trustHolds ? score.heldToEnd : 0);
  const durationNotEvaluated = !trustHolds && score.heldToEnd > 0;

  return [
    {
      key: 'note',
      label: 'Nota (altura)',
      icon: 'ri-music-2-line',
      correct: rightPitch,
      wrong: score.wrongNote + score.missedNotes,
      applicable: true,
      notEvaluated: false,
      rule: 'Certo quando você toca a tecla da nota escrita. Errado quando toca outra nota ou quando a nota escrita não é tocada (altura não confere).',
    },
    {
      key: 'attack',
      label: 'Ataque (momento)',
      icon: 'ri-timer-line',
      correct: score.correct,
      wrong: score.early + score.late,
      applicable: true,
      notEvaluated: false,
      rule: 'Certo quando o ataque cai no pulso. Errado quando foge da tolerância (cedo ou atrasado) — sem exigir precisão de milissegundos, sobretudo no celular.',
    },
    {
      key: 'duration',
      label: 'Duração',
      icon: 'ri-hourglass-line',
      correct: durationCorrect,
      wrong: score.durationBreak,
      applicable: context.measuresDuration,
      notEvaluated: durationNotEvaluated,
      rule: 'Certo quando a tecla fica pressionada até perto do fim da figura escrita — ao menos 80% dela e nunca mais de 0,25 pulso cedo demais. Vale para o grupo todo (nota-base + ligaduras). Errado quando solta antes disso ou quando um novo toque corta a figura. Se o aparelho não informar o fim da nota (soltar a tecla / Note Off), a duração fica NÃO AVALIADA, não "correta".',
    },
    {
      key: 'rest',
      label: 'Pausa',
      icon: 'ri-pause-circle-line',
      correct: score.restsHeld,
      wrong: score.restIntrusion,
      applicable: context.hasRests,
      notEvaluated: false,
      rule: 'Certo quando você fica em silêncio durante a pausa inteira. Errado quando toca em cima da pausa.',
    },
    {
      key: 'tie',
      label: 'Ligadura',
      icon: 'ri-link',
      correct: score.tiesHeld,
      wrong: score.tieReattack,
      applicable: context.hasTies,
      notEvaluated: false,
      rule: 'Certo quando você segura o som e não ataca a segunda nota da ligadura. Errado quando ataca de novo sobre a ligadura.',
    },
  ];
}

export interface RhythmItemAssessment {
  key: RhythmItemKey;
  label: string;
  correct: number;
  wrong: number;
  notEvaluated: boolean;
}

export interface RhythmAssessment {
  /** itens que ESTA aula cobra (o critério de domínio se aplica a eles) */
  applicableKeys: RhythmItemKey[];
  /** leitura item por item (já filtrada pelos aplicáveis) */
  items: RhythmItemAssessment[];
  /** itens aplicáveis que ficaram abaixo da meta de domínio */
  failedKeys: RhythmItemKey[];
  /** itens aplicáveis que não puderam ser medidos */
  notEvaluatedKeys: RhythmItemKey[];
  /** houve algum toque avaliado? (execução de verdade, e não sessão vazia) */
  hasInput: boolean;
  /**
   * Execução completa SEM erros nos itens que a aula ensina (mesmo que algum
   * item não tenha sido medido). É o estado "tentativa sem erros".
   */
  errorFree: boolean;
  /**
   * Aula dominada = execução sem erros E com todos os itens aplicáveis
   * efetivamente medidos. É exatamente o critério que desbloqueia a próxima
   * aula; quando algo não pôde ser medido, a tentativa fica sem erros mas não
   * conta como dominada.
   */
  mastered: boolean;
}

/**
 * Avalia uma execução de ritmo pelo critério de domínio do curso.
 *
 * Diferencia os três estados que o aluno vê:
 *   - executou com erros  → `errorFree === false` (ainda que a precisão % seja alta);
 *   - executou sem erros    → `errorFree === true` (estado "tentativa sem erros");
 *   - não mediu algum item  → `notEvaluatedKeys` preenchido: fica sem erros, mas
 *     NÃO é dado como dominado.
 *
 * A precisão em % continua sendo exibida como resultado da tentativa, mas NÃO
 * desbloqueia nada: só `mastered` faz isso.
 */
export function assessRhythmAttempt(
  score: RhythmScore,
  context: RhythmSummaryContext,
): RhythmAssessment {
  const applicable = buildRhythmSummary(score, context).filter((item) => item.applicable);
  const items: RhythmItemAssessment[] = applicable.map((item) => ({
    key: item.key,
    label: item.label,
    correct: item.correct,
    wrong: item.wrong,
    notEvaluated: item.notEvaluated,
  }));
  const accuracyOf = (item: RhythmItemAssessment) => {
    const total = item.correct + item.wrong;
    return total > 0 ? Math.round((item.correct / total) * 100) : 0;
  };
  const failedKeys = items
    .filter((item) => !item.notEvaluated && accuracyOf(item) < RHYTHM_ACCURACY_GOAL)
    .map((item) => item.key);
  const notEvaluatedKeys = items.filter((item) => item.notEvaluated).map((item) => item.key);
  const hasInput = score.attempts > 0;
  const errorFree = hasInput && items.every((item) => item.wrong === 0);
  const mastered =
    hasInput &&
    notEvaluatedKeys.length === 0 &&
    items.every((item) => accuracyOf(item) >= RHYTHM_ACCURACY_GOAL);
  return {
    applicableKeys: applicable.map((item) => item.key),
    items,
    failedKeys,
    notEvaluatedKeys,
    hasInput,
    errorFree,
    mastered,
  };
}

export const RHYTHM_ITEM_LABEL: Record<RhythmItemKey, string> = {
  note: 'Nota',
  attack: 'Ataque',
  duration: 'Duração',
  rest: 'Pausa',
  tie: 'Ligadura',
};